
import { useState } from 'react';

export default function SystemTab({ showSuccess }: { showSuccess: (msg: string) => void }) {
  const [autoBackup, setAutoBackup] = useState(true);
  const [emailNotification, setEmailNotification] = useState(true);
  const [certReminder, setCertReminder] = useState(true);
  const [renewReminder, setRenewReminder] = useState(true);
  const [dataRetention, setDataRetention] = useState('3年');
  const [backupFreq, setBackupFreq] = useState('每日');

  // 续费信息
  const [renewInfo] = useState({
    productName: '粮食台账管理系统',
    version: '专业版',
    expireDate: '2026-08-15',
    activationDate: '2025-08-15',
    licenseCode: 'GRAIN-PRO-2025-XXXXXX',
    maxUsers: 20,
    currentUsers: 5,
    maxStations: 10,
    currentStations: 3,
    servicePhone: '400-888-8888',
    serviceEmail: 'support@grain-sys.com',
  });

  const getDaysUntilExpiry = (dateStr: string) => {
    const today = new Date();
    const expiry = new Date(dateStr);
    return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const daysLeft = getDaysUntilExpiry(renewInfo.expireDate);
  const isExpiringSoon = daysLeft <= 90;
  const isExpired = daysLeft < 0;

  return (
    <div className="space-y-6">
      {/* 系统续费提醒 */}
      <div
        className={`rounded-xl border-2 overflow-hidden ${
          isExpired ? 'border-rose-300' : isExpiringSoon ? 'border-amber-300' : 'border-teal-200'
        }`}
      >
        <div
          className={`px-6 py-4 flex items-center justify-between ${
            isExpired ? 'bg-rose-50' : isExpiringSoon ? 'bg-amber-50' : 'bg-teal-50'
          }`}
        >
          <div className="flex items-center space-x-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isExpired ? 'bg-rose-100' : isExpiringSoon ? 'bg-amber-100' : 'bg-teal-100'
              }`}
            >
              <i
                className={`ri-shield-check-line text-xl ${
                  isExpired ? 'text-rose-600' : isExpiringSoon ? 'text-amber-600' : 'text-teal-600'
                }`}
              ></i>
            </div>
            <div>
              <p
                className={`text-sm font-semibold ${
                  isExpired ? 'text-rose-700' : isExpiringSoon ? 'text-amber-700' : 'text-teal-700'
                }`}
              >
                {isExpired ? '⚠️ 系统授权已过期' : isExpiringSoon ? '⏰ 系统授权即将到期' : '✅ 系统授权正常'}
              </p>
              <p
                className={`text-xs mt-0.5 ${
                  isExpired ? 'text-rose-600' : isExpiringSoon ? 'text-amber-600' : 'text-teal-600'
                }`}
              >
                {isExpired
                  ? `授权已过期 ${Math.abs(daysLeft)} 天，请尽快续费以恢复正常使用`
                  : isExpiringSoon
                  ? `授权将在 ${daysLeft} 天后到期，建议提前续费避免影响使用`
                  : `授权有效，还剩 ${daysLeft} 天到期`}
              </p>
            </div>
          </div>
          <button
            className={`px-5 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
              isExpired
                ? 'bg-rose-600 text-white hover:bg-rose-700'
                : isExpiringSoon
                ? 'bg-amber-500 text-white hover:bg-amber-600'
                : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}
          >
            <i className="ri-refresh-line mr-2"></i>立即续费
          </button>
        </div>

        <div className="bg-white p-6">
          <div className="grid grid-cols-3 gap-6 mb-5">
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-1">产品版本</p>
              <p className="text-base font-bold text-gray-800">{renewInfo.version}</p>
              <p className="text-xs text-gray-400 mt-0.5">{renewInfo.productName}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-1">授权到期</p>
              <p
                className={`text-base font-bold ${
                  isExpired ? 'text-rose-600' : isExpiringSoon ? 'text-amber-600' : 'text-gray-800'
                }`}
              >
                {renewInfo.expireDate}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">激活于 {renewInfo.activationDate}</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-1">授权码</p>
              <p className="text-sm font-mono font-bold text-gray-700">{renewInfo.licenseCode}</p>
              <p className="text-xs text-gray-400 mt-0.5">正版授权</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="p-4 border border-gray-200 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">用户数量</p>
                <span className="text-xs text-gray-400">
                  {renewInfo.currentUsers} / {renewInfo.maxUsers}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-teal-500 h-2 rounded-full transition-all"
                  style={{
                    width: `${(renewInfo.currentUsers / renewInfo.maxUsers) * 100}%`,
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                已使用 {renewInfo.currentUsers} 个，剩余 {renewInfo.maxUsers - renewInfo.currentUsers}{' '}
                个
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">站点数量</p>
                <span className="text-xs text-gray-400">
                  {renewInfo.currentStations} / {renewInfo.maxStations}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-amber-500 h-2 rounded-full transition-all"
                  style={{
                    width: `${(renewInfo.currentStations / renewInfo.maxStations) * 100}%`,
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                已使用 {renewInfo.currentStations} 个，剩余{' '}
                {renewInfo.maxStations - renewInfo.currentStations} 个
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <span className="flex items-center space-x-1.5">
                <i className="ri-phone-line text-teal-500"></i>
                <span>
                  服务热线：<strong className="text-gray-700">{renewInfo.servicePhone}</strong>
                </span>
              </span>
              <span className="flex items-center space-x-1.5">
                <i className="ri-mail-line text-teal-500"></i>
                <span>
                  服务邮箱：<strong className="text-gray-700">{renewInfo.serviceEmail}</strong>
                </span>
              </span>
            </div>
            <button className="px-4 py-2 border border-teal-300 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-customer-service-line mr-1"></i>联系客服
            </button>
          </div>
        </div>
      </div>

      {/* 系统偏好设置 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center">
          <i className="ri-toggle-line mr-2 text-teal-600"></i>系统偏好设置
        </h3>
        <div className="space-y-3">
          {[
            {
              key: 'autoBackup',
              label: '自动数据备份',
              desc: '每日凌晨自动备份台账数据',
              value: autoBackup,
              toggle: () => setAutoBackup(!autoBackup),
            },
            {
              key: 'emailNotification',
              label: '邮件通知',
              desc: '重要操作和异常情况邮件提醒',
              value: emailNotification,
              toggle: () => setEmailNotification(!emailNotification),
            },
            {
              key: 'certReminder',
              label: '证件到期提醒',
              desc: '证件到期前90天自动提醒管理员',
              value: certReminder,
              toggle: () => setCertReminder(!certReminder),
            },
            {
              key: 'renewReminder',
              label: '系统续费提醒',
              desc: '授权到期前90天弹出续费提醒',
              value: renewReminder,
              toggle: () => setRenewReminder(!renewReminder),
            },
          ].map((item) => (
            <div
              key={item.key}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-teal-200 transition-all"
            >
              <div>
                <p className="text-sm font-medium text-gray-800">{item.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
              <button
                onClick={item.toggle}
                className={`relative w-12 h-6 rounded-full transition-all cursor-pointer flex-shrink-0 ${
                  item.value ? 'bg-teal-600' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${
                    item.value ? 'translate-x-6' : 'translate-x-0'
                  }`}
                ></div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* 数据管理设置 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center">
          <i className="ri-database-2-line mr-2 text-teal-600"></i>数据管理设置
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">数据保留周期</label>
            <select
              value={dataRetention}
              onChange={(e) => setDataRetention(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
            >
              <option>1年</option>
              <option>3年</option>
              <option>5年</option>
              <option>永久保留</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">备份频率</label>
            <select
              value={backupFreq}
              onChange={(e) => setBackupFreq(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
            >
              <option>每日</option>
              <option>每周</option>
              <option>每月</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div>
            <p className="text-sm font-medium text-gray-700">手动备份</p>
            <p className="text-xs text-gray-500 mt-0.5">立即创建一份完整的数据备份</p>
          </div>
          <button
            onClick={() => showSuccess('数据备份已启动，请稍候...')}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-save-3-line mr-2"></i>立即备份
          </button>
        </div>
      </div>

      {/* 系统信息 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center">
          <i className="ri-information-line mr-2 text-teal-600"></i>系统信息
        </h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: '系统版本', value: 'V1.0.8' },
            { label: '数据库版本', value: 'PostgreSQL 15.2' },
            { label: '最后更新', value: '2026-01-20' },
            { label: '运行环境', value: 'Web / 浏览器' },
            { label: '数据总量', value: '12,480 条记录' },
            { label: '存储占用', value: '256 MB' },
          ].map((item, i) => (
            <div key={i} className="p-3 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-400">{item.label}</p>
              <p className="text-sm font-semibold text-gray-700 mt-0.5">{item.value}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-end">
          <button
            onClick={() => showSuccess('已检查更新，当前为最新版本')}
            className="px-5 py-2 border border-teal-300 text-teal-700 rounded-lg hover:bg-teal-50 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line mr-2"></i>检查更新
          </button>
        </div>
      </div>
    </div>
  );
}
